// ============================================
// useTradeHistory — Helius parsed swap history with localStorage cache
// Fetches on wallet connect, incremental updates on revisit
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react'
import { dbg } from '../../utils/debugFlag'

// Helius access goes through the auth-gated Pages Function proxy
// (functions/api/helius-v0/[[path]].js). It injects the server-side
// HELIUS_API_KEY and strips any client-supplied key — the frontend never
// sees or needs the key. Relative URL = same origin, so the session
// cookie the proxy's auth gate requires is sent automatically. The
// function prefixes /v0/ upstream, so paths here must NOT include it.
const HELIUS_BASE = '/api/helius-v0'
const PAGE_LIMIT = 100
// Time-bounded history: on a full fetch we page back until trades cross the
// lookback window (a fixed tx count could be just a few days on an active
// wallet). MAX_PAGES is a safety ceiling so a hyperactive wallet can't loop
// forever — 30 pages = up to 3000 swaps.
const LOOKBACK_DAYS = 30
const MAX_PAGES = 30
const CACHE_KEY = (wallet) => `solcheddar_tradehistory_${wallet.slice(0, 8)}`

// ─── Parse a Helius enriched swap into a clean trade record ─────────
//
// Verified against real PUMP_AMM / JUPITER payloads. Key lessons:
//   • events.swap is unreliable — ignore it.
//   • nativeBalanceChange is NET of rent + temp-WSOL-account reclaim, so it
//     can even flip sign on a buy. Never use it for amount or direction.
//   • The trustworthy signal is tokenTransfers filtered to the wallet:
//       - the non-WSOL leg gives the token + DIRECTION
//         (wallet receives token = BUY, wallet sends token = SELL)
//       - the WSOL leg (or the largest native transfer leg) gives the SOL
//         amount — this is the actual swap leg, not the rent dust.
//   • description, when present, is Helius's own correct parse — used as a
//     final fallback ("swapped X MINT for Y SOL").

const WSOL_MINT = 'So11111111111111111111111111111111111111112'
const LAMPORTS = 1e9
const RENT_DUST = 0.01  // ignore native legs at/below this (ATA rent ~0.00204)

function parseSwap(tx, wallet) {
  const sig = tx.signature
  const timestamp = (tx.timestamp || 0) * 1000
  const source = tx.source || 'unknown'
  const description = tx.description || ''

  const tokenTransfers = tx.tokenTransfers || []
  const nativeTransfers = tx.nativeTransfers || []

  // 1. Token leg (non-WSOL) — direction + amount. Pick the largest-magnitude
  //    transfer that involves the wallet.
  let tokenDelta = 0, mint = null, decimals = 0
  for (const tt of tokenTransfers) {
    if (tt.mint === WSOL_MINT) continue
    const toW = tt.toUserAccount === wallet
    const frW = tt.fromUserAccount === wallet
    if (!toW && !frW) continue
    const amt = Number(tt.tokenAmount || 0)
    if (Math.abs(amt) > Math.abs(tokenDelta)) {
      tokenDelta = toW ? amt : -amt
      mint = tt.mint
    }
  }

  // 2. SOL amount — prefer the WSOL token leg involving the wallet…
  let solAmount = 0
  for (const tt of tokenTransfers) {
    if (tt.mint !== WSOL_MINT) continue
    if (tt.toUserAccount !== wallet && tt.fromUserAccount !== wallet) continue
    solAmount = Math.max(solAmount, Number(tt.tokenAmount || 0))
  }
  // …else the largest native transfer leg involving the wallet (skip rent dust).
  if (solAmount === 0) {
    let maxLeg = 0
    for (const nt of nativeTransfers) {
      if (nt.toUserAccount !== wallet && nt.fromUserAccount !== wallet) continue
      const sol = (nt.amount || 0) / LAMPORTS
      if (sol > maxLeg) maxLeg = sol
    }
    if (maxLeg > RENT_DUST) solAmount = maxLeg
  }

  // 3. Fallback to Helius's own parsed description if structured legs missing.
  if (!mint || tokenDelta === 0) {
    // "<wallet> swapped <tokAmt> <MINT> for <solAmt> SOL"  → SELL
    // "<wallet> swapped <solAmt> SOL for <tokAmt> <MINT>"  → BUY
    const sellM = description.match(/swapped ([\d.]+) ([1-9A-HJ-NP-Za-km-z]{32,}) for ([\d.]+) SOL/)
    const buyM  = description.match(/swapped ([\d.]+) SOL for ([\d.]+) ([1-9A-HJ-NP-Za-km-z]{32,})/)
    if (sellM) {
      return finalize({ sig, timestamp, side: 'sell', mint: sellM[2],
        tokenAmount: +sellM[1], solAmount: +sellM[3], decimals: 0, source, description })
    }
    if (buyM) {
      return finalize({ sig, timestamp, side: 'buy', mint: buyM[3],
        tokenAmount: +buyM[2], solAmount: +buyM[1], decimals: 0, source, description })
    }
    return null
  }

  // Direction from the token leg: received token = buy, sent = sell.
  const side = tokenDelta > 0 ? 'buy' : 'sell'
  return finalize({
    sig, timestamp, side, mint,
    tokenAmount: Math.abs(tokenDelta), solAmount, decimals, source, description,
  })
}

function finalize({ sig, timestamp, side, mint, tokenAmount, solAmount, decimals, source, description }) {
  return {
    signature: sig,
    timestamp,
    side,
    mint,
    tokenAmount,
    solAmount,
    decimals,
    source,
    description,
  }
}

// ─── Fetch pages from Helius ────────────────────────────────────────

async function fetchSwapHistory(wallet, untilSig = null) {
  const trades = []
  let lastSig = null
  const cutoffMs = Date.now() - LOOKBACK_DAYS * 24 * 60 * 60 * 1000

  for (let page = 0; page < MAX_PAGES; page++) {
    let url = `${HELIUS_BASE}/addresses/${wallet}/transactions/?type=SWAP&limit=${PAGE_LIMIT}`
    if (lastSig) url += `&before=${lastSig}`

    try {
      const resp = await fetch(url)
      if (!resp.ok) { console.warn(`[TradeHistory] Helius ${resp.status}`); break }

      const txs = await resp.json()
      if (!txs || txs.length === 0) break

      let crossedCutoff = false
      for (const tx of txs) {
        // Stop if we've reached a previously cached signature
        if (untilSig && tx.signature === untilSig) return trades
        // Helius returns newest-first; once a tx predates the lookback
        // window, everything after it is older too — stop here.
        if (tx.timestamp && tx.timestamp * 1000 < cutoffMs) { crossedCutoff = true; break }
        const parsed = parseSwap(tx, wallet)
        if (parsed) trades.push(parsed)
      }
      if (crossedCutoff) break

      // NOTE: do NOT break on a short page. With the `type=SWAP` filter,
      // Helius returns only swap txs from each scan window, so pages are
      // routinely shorter than PAGE_LIMIT even when much more history exists
      // further back. Breaking on `length < PAGE_LIMIT` was capping history
      // at the first page (~the most recent few days). Instead keep paging by
      // `before` until an EMPTY page (true end of history), the time cutoff,
      // or MAX_PAGES. We still advance the cursor by the last tx's signature.
      lastSig = txs[txs.length - 1].signature
    } catch (err) {
      console.warn('[TradeHistory] Fetch failed:', err)
      break
    }
  }

  return trades
}

// ─── Hook ───────────────────────────────────────────────────────────

export default function useTradeHistory(publicKey) {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const fetchedRef = useRef(null)

  const wallet = publicKey?.toString() || null

  // Load from cache immediately
  useEffect(() => {
    if (!wallet) { setHistory([]); return }
    try {
      const cached = localStorage.getItem(CACHE_KEY(wallet))
      if (cached) setHistory(JSON.parse(cached))
    } catch {}
  }, [wallet])

  // Fetch from Helius (incremental)
  const refresh = useCallback(async (force = false) => {
    if (!wallet) return
    if (!force && fetchedRef.current === wallet) return

    setLoading(true)
    try {
      let cached = []
      try {
        const raw = localStorage.getItem(CACHE_KEY(wallet))
        if (raw) cached = JSON.parse(raw)
      } catch {}

      // If the cache doesn't reach back to the lookback window, the oldest
      // cached trade is newer than the cutoff — an incremental fetch (which
      // stops at the cached newest sig) would never back-fill the gap. In
      // that case fetch the FULL window. This self-heals caches built when
      // the limit was a small tx count rather than a time window.
      //
      // `backfilledThrough` is the OLDEST timestamp we've paged back to on a
      // prior full fetch. We only need another full fetch if the current
      // cutoff is older (smaller) than that — i.e. the window now reaches
      // further back than we've covered. As time passes the cutoff moves
      // FORWARD (newer), so a young wallet stays on the incremental path.
      const cutoffMs = Date.now() - LOOKBACK_DAYS * 24 * 60 * 60 * 1000
      let backfilledThrough = Infinity
      try {
        const bf = localStorage.getItem(CACHE_KEY(wallet) + '_bf')
        if (bf != null) backfilledThrough = Number(bf)
      } catch {}
      const doFull = force || !Number.isFinite(backfilledThrough) || cutoffMs < backfilledThrough

      const newestSig = cached.length > 0 ? cached[0].signature : null
      const newTrades = await fetchSwapHistory(wallet, doFull ? null : newestSig)

      const sigSet = new Set(newTrades.map(t => t.signature))
      const merged = [...newTrades, ...cached.filter(t => !sigSet.has(t.signature))]
      merged.sort((a, b) => b.timestamp - a.timestamp)

      try {
        localStorage.setItem(CACHE_KEY(wallet), JSON.stringify(merged))
        // After a full fetch we've paged back to (at least) the cutoff —
        // record it so future reloads can go incremental.
        if (doFull) localStorage.setItem(CACHE_KEY(wallet) + '_bf', String(cutoffMs))
      } catch {}

      setHistory(merged)
      fetchedRef.current = wallet
      dbg(`[TradeHistory] ${newTrades.length} new, ${merged.length} total`)
    } catch (err) {
      console.warn('[TradeHistory] refresh failed:', err)
    }
    setLoading(false)
  }, [wallet])

  // Auto-fetch on wallet connect
  useEffect(() => {
    if (wallet) refresh()
  }, [wallet, refresh])

  // Helper: get buy entries for a specific mint (for backfilling positions)
  const getEntriesForMint = useCallback((mint) => {
    return history.filter(t => t.mint === mint && t.side === 'buy')
  }, [history])

  return { history, loading, refresh, getEntriesForMint }
}